<template>
  <!-- Marketing-Kennzahl als StatCard (Jira-Summary): Icon je Kennzahl, Definition im ⓘ-Tooltip,
       Meta-Zeile = Formel oder Zusatzzahl. Fehlende Kennzahlen bleiben als "nicht erfasst" stehen. -->
  <StatCard
    :label="label || card?.label || ''"
    :value="hint || formatKpi(card)"
    :meta="resolvedMeta"
    :icon="iconFor.icon"
    :tone="iconFor.tone"
    :muted="Boolean(hint)"
  >
    <template #info>
      <InfoTooltip
        v-if="definition"
        :title="label || card?.label"
        :text="definition.definition_de || ''"
        :formula="definition.formula_de ? `Formel: ${definition.formula_de}` : ''"
        :items="tooltipItems"
      />
    </template>
  </StatCard>
</template>

<script setup>
import { computed, inject } from 'vue';
import StatCard from '@/components/ui/StatCard.vue';
import { formatKpi } from '../composables/formatters';
import InfoTooltip from './InfoTooltip.vue';

const props = defineProps({
  card: { type: Object, default: null },
  label: { type: String, default: '' },
  meta: { type: String, default: '' },
  definitionKey: { type: String, default: '' },
  hint: { type: String, default: '' },
  tooltipItems: { type: Array, default: () => [] }
});

// Icon je Kennzahl-Schlüssel (API-kpi_key). Unbekannte Schlüssel fallen nach Format zurück.
const ICONS = {
  projects: { icon: 'fa-folder', tone: 'gray' },
  campaigns: { icon: 'fa-bullhorn', tone: 'blue' },
  measures: { icon: 'fa-list-check', tone: 'purple' },
  gross_value: { icon: 'fa-euro-sign', tone: 'teal' },
  net_value: { icon: 'fa-receipt', tone: 'green' },
  impressions: { icon: 'fa-eye', tone: 'blue' },
  impressions_or_reach: { icon: 'fa-eye', tone: 'blue' },
  clicks: { icon: 'fa-arrow-pointer', tone: 'teal' },
  ctr: { icon: 'fa-percent', tone: 'purple' },
  posts: { icon: 'fa-pen-to-square', tone: 'gray' },
  engagements: { icon: 'fa-heart', tone: 'purple' },
  engagement_rate: { icon: 'fa-percent', tone: 'green' },
  open_rate: { icon: 'fa-envelope-open', tone: 'blue' },
  click_rate: { icon: 'fa-arrow-pointer', tone: 'teal' },
  ctor: { icon: 'fa-percent', tone: 'purple' },
  sessions: { icon: 'fa-users', tone: 'blue' },
  conversions: { icon: 'fa-bullseye', tone: 'green' },
  conversion_rate: { icon: 'fa-percent', tone: 'orange' },
  placements: { icon: 'fa-newspaper', tone: 'blue' },
  titles: { icon: 'fa-book-open', tone: 'purple' },
  issues: { icon: 'fa-layer-group', tone: 'gray' },
  internal: { icon: 'fa-house', tone: 'teal' },
  barter: { icon: 'fa-handshake', tone: 'orange' }
};

const iconFor = computed(() => {
  const key = props.definitionKey || props.card?.key;
  if (ICONS[key]) return ICONS[key];
  if (props.card?.format === 'percent') return { icon: 'fa-percent', tone: 'purple' };
  if (props.card?.format === 'currency') return { icon: 'fa-euro-sign', tone: 'teal' };
  return { icon: 'fa-hashtag', tone: 'gray' };
});

// Definitionen werden vom Seiten-Template bereitgestellt (kpi_key → Definition).
const definitions = inject('mbKpiDefinitions', computed(() => ({})));

const definition = computed(() => {
  const own = (definitions.value || {})[props.definitionKey || props.card?.key];
  return own || (props.tooltipItems.length ? { definition_de: '' } : null);
});

// Ohne eigene Meta-Zeile: Formel aus der Definition (z. B. "Klicks ÷ gelieferte Impressions").
const resolvedMeta = computed(() => {
  if (props.meta) return props.meta;
  const formula = definition.value?.formula_de;
  return formula ? formula.replace(/\s\/\s/g, ' ÷ ') : '';
});
</script>
